import { useWebSocket } from "contexts/WebSocketContext";
import { useEffect, useRef } from "react";

/**
 * Hook for subscribing to specific WebSocket events
 * This is the recommended way to listen for WebSocket events in components
 *
 * @param eventType - The event type to listen for (e.g., 'encounter_processing_finished')
 * @param callback - Function to call when event is received
 * @param dependencies - Dependencies array for the callback (optional)
 *
 * @example
 * const MyComponent = () => {
 *   const handleEncounterComplete = (message) => {
 *     console.log('Encounter processing complete:', message);
 *   };
 *
 *   useWebSocketEvent('encounter_processing_finished', handleEncounterComplete);
 * };
 */
export const useWebSocketEvent = <T = any>(
  eventType: string,
  callback: (message: T) => void,
  dependencies: React.DependencyList = []
): void => {
  const { subscribeToEvent } = useWebSocket();
  const callbackRef = useRef<(message: T) => void>(callback);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!eventType) return;

    const unsubscribe = subscribeToEvent(eventType, (message) => {
      callbackRef.current(message as T);
    });

    return unsubscribe;
  }, [eventType, subscribeToEvent, ...dependencies]);
};
